import Vue from 'vue';
import apiClient from '@/apiClient';

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

import { dateToLocal } from '@/modules/dates';
import config from '@/config';
import _ from 'lodash';
import { compareDesc, parse } from 'date-fns';
import formatDateFilter from '@/filters/format-date.filter';

const { notificationsAuthAddress, wsKey, wsHost, wsPort, wssPort } = config;

const state = {
  wsClient: null,
  persistReconnect: false,
  notifications: [],
  sortedNotifications: {},
  notificationsPage: 1,
  isNotificationsLoading: false
};

const utils = {
  sortNotifications(notifications) {
    const list = _.uniqBy(notifications, notification => notification.id);
    const sortedList = list.sort(function(a, b) {
      const dateLeft = parse(
        a.created_at,
        config.dateTimeInputFormat,
        new Date()
      );
      const dateRight = parse(
        b.created_at,
        config.dateTimeInputFormat,
        new Date()
      );
      return compareDesc(dateLeft, dateRight);
    });
    const groupedList = _.groupBy(sortedList, item => {
      return item.created_at.split(' ')[0];
    });
    const lastDay = sortedList[0].created_at.split(' ')[0];
    if (state.sortedNotifications[lastDay]) {
      groupedList[lastDay] = state.sortedNotifications[lastDay].concat(
        groupedList[lastDay]
      );
    }
    return groupedList;
  }
};

const actions = {
  setConnectionWithSocket: ({ commit, dispatch }, userId) => {
    const e = new Echo({
      broadcaster: 'pusher',
      disableStats: true,
      key: wsKey,
      wsHost,
      wssPort,
      wsPort,
      encrypted: true,
      authEndpoint: notificationsAuthAddress,
      auth: {
        headers: {
          authorization: `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      }
    });
    commit('setWsClient', e);

    e.connector.pusher.connection.bind('connected', () => {
      console.log('ws connected');
      commit('dropReconnect');
      e.private(`users.${userId}`).notification(notification => {
        commit('addNotification', notification);
      });
    });
    e.connector.pusher.connection.bind('state_change', states => {
      if (states.current === 'unavailable') {
        commit('persistReconnect');
      }
    });
    e.connector.pusher.connection.bind('disconnected', () => {
      commit('clearWsClient');
      console.log('ws disconnected');
      if (state.persistReconnect) {
        dispatch('setConnectionWithSocket', userId);
      }
    });
    e.connector.pusher.connect();
  },

  disableConnectionWithSocket: ({ commit }) => {
    if (!state.wsClient) return;
    commit('dropReconnect');
    state.wsClient.connector.pusher.disconnect();
  },

  getNotifications: ({ commit }) => {
    commit('setNotificationsLoading', true);
    new Promise((resolve, reject) => {
      const { perPage } = config.websocketNotifications;
      apiClient
        .get(
          `/notifications?per_page=${perPage}&page=${state.notificationsPage}`
        )
        .then(response => {
          const notifications = response.data;
          // Dates to local timezone
          notifications.forEach(item => {
            const dateFields = (({ created_at }) => ({
              created_at
            }))(item);
            for (const [key, value] of Object.entries(dateFields)) {
              item[key] = dateToLocal(
                value,
                config.dateTimeInputFormat,
                config.dateTimeInputFormat
              );
              item['hours'] = formatDateFilter(item[key], 'HH:mm', false);
            }
          });
          const sortedNotifications = utils.sortNotifications(notifications);
          commit('setNotifications', notifications);
          commit('setSortedNotifications', sortedNotifications);
          commit('setNotificationsLoading', false);
          resolve(response);
        })
        .catch(err => {
          commit('setNotificationsLoading', false);
          reject(err);
        });
    });
  },

  setViewedToNotification: (_context, { id, viewed }) =>
    new Promise((resolve, reject) => {
      apiClient
        .patch(`/notifications/${id}`, { viewed })
        .then(response => {
          state.notifications.find(notification => {
            return notification.id === id;
          }).viewed = viewed;
          Vue.set(state, 'notifications', state.notifications);
          resolve(response);
        })
        .catch(err => {
          reject(err);
        });
    }),

  resetNotification: ({ commit }) => {
    commit('resetNotifications');
  }
};

const mutations = {
  setNotifications: (state, notifications) => {
    // Vue.set(state, 'notifications', notifications);
    Vue.set(state, 'notifications', state.notifications.concat(notifications));
    if (notifications.length < config.websocketNotifications.perPage) {
      Vue.set(state, 'notificationsPage', 0);
    } else {
      Vue.set(state, 'notificationsPage', state.notificationsPage + 1);
    }
  },

  setSortedNotifications: (state, sortedNotifications) => {
    const fullSortedNotifications = Object.assign(
      state.sortedNotifications,
      sortedNotifications
    );
    delete fullSortedNotifications['__ob__'];
    Vue.set(state, 'sortedNotifications', fullSortedNotifications);
  },

  setNotificationsLoading: (state, isLoading) => {
    Vue.set(state, 'isNotificationsLoading', isLoading);
  },

  addNotification: (state, notification) => {
    Vue.set(state, 'notifications', state.notifications.concat(notification));
  },

  resetNotifications: state => {
    Vue.set(state, 'sortedNotifications', {});
    Vue.set(state, 'notifications', []);
    Vue.set(state, 'notificationsPage', 1);
  },

  setWsClient: (state, client) => {
    Vue.set(state, 'wsClient', client);
  },

  clearWsClient: state => {
    Vue.set(state, 'wsClient', null);
  },

  persistReconnect: state => {
    Vue.set(state, 'persistReconnect', true);
  },

  dropReconnect: state => {
    Vue.set(state, 'persistReconnect', false);
  }
};

export default {
  state,
  actions,
  mutations
};
